/*
 * The Images card: the rows in kb.ArticleImage, which belong to the article
 * rather than to any one revision.
 *
 * Moved out of ArticleEditorPage when the bulk delete arrived, so that it can be
 * tested on its own: a button that removes several attachments at once is not
 * something to ship on the strength of a type-check. The single-image flow is
 * exactly as it was; the move changed its address and nothing else.
 *
 * A release note's Images card is this one too (S42, 2026-10-12): it takes an
 * `imageOwner` (imageOwner.ts) in place of the `articleId` shorthand, and every
 * call, the ceiling in the header and the sentences that name the owner come
 * from it. With `articleId` every string and call is what it was.
 *
 * Its words come from the chosen language's dictionary, t.admin.editor.images (S48).
 */

import { useMemo, useState } from 'react';
import type { AdminArticleImageBulkDeleteResponse, ArticleImage } from '@accotool-kb/shared';
import { countArticleImageReferences } from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction } from '../../hooks/useAsync.js';
import { DateTime, ErrorBanner, Modal } from '../../components/ui.js';
import type { Messages } from '../../i18n/en.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';
import { useImagePreviews } from './useArticleImagePreviews.js';
import { useRevealedRows } from './useRevealedRows.js';
import { useHoverPreview } from './useHoverPreview.js';
import { ImageHoverPreview } from './ImageHoverPreview.js';
import { articleImageOwner, type ImageOwner } from './imageOwner.js';

/**
 * Whose images: an article by id (the shorthand every article call site uses), or
 * any owner by adapter. Exactly one of the two.
 */
type ImagesPanelOwner =
  | { articleId: string; imageOwner?: undefined }
  | { imageOwner: ImageOwner; articleId?: undefined };

/**
 * The article's attachments, with the two things an author needs to do to them:
 * put a reference back into the text, and get rid of one.
 *
 * Uploading is not here. An image arrives by being pasted or dropped into the text,
 * which is where the reference has to end up anyway; a separate upload form would
 * produce attachments nothing mentions, which is exactly what the "not used" badge
 * below exists to complain about.
 *
 * The usage count is computed from the text in the editor, not from the
 * referenceCount the server sent. The server counts the SAVED revision, which is the
 * right answer to a different question: an image referenced by a paragraph typed two
 * minutes ago is in use, and one whose only reference was just deleted is not. This
 * count is the one the delete warning has to be based on, because it describes what
 * the author is actually looking at.
 *
 * The bulk delete removes every row that count calls unused, in one dialog and one
 * request, because an article whose author pastes generously ends up with a dozen
 * orphaned screenshots and a dozen confirmations is how they stay attached. Same
 * set as the badge, by construction: both read the same map.
 */
export function ArticleImagesPanel({
  articleId,
  imageOwner,
  content,
  images,
  onImagesChanged,
  onInsert,
}: ImagesPanelOwner & {
  content: string;
  images: ArticleImage[];
  onImagesChanged: (next: ArticleImage[]) => void;
  onInsert: (fileName: string) => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.editor.images;
  const api = useAdminApi();
  const action = useAction();

  const owner = useMemo(
    () => imageOwner ?? articleImageOwner(api, articleId as string),
    [imageOwner, api, articleId],
  );
  const noun = owner.noun;

  const [pendingDelete, setPendingDelete] = useState<ArticleImage | null>(null);
  const [deletedNotice, setDeletedNotice] = useState<string | null>(null);
  const [pendingBulk, setPendingBulk] = useState(false);

  const totalBytes = images.reduce((sum, image) => sum + image.byteLength, 0);

  const referenceCounts = useMemo(
    () =>
      new Map(
        images.map((image) => [
          image.fileName,
          countArticleImageReferences(content, image.fileName),
        ]),
      ),
    [images, content],
  );

  const pendingReferences = pendingDelete ? (referenceCounts.get(pendingDelete.fileName) ?? 0) : 0;

  /*
   * The attachments nothing in the editor's text refers to - the rows wearing
   * the "not used" badge, and exactly the set the bulk delete offers to remove.
   * One map and one definition for both, so the button cannot delete an image
   * the badge beside it calls used.
   */
  const unused = useMemo(
    () => images.filter((image) => (referenceCounts.get(image.fileName) ?? 0) === 0),
    [images, referenceCounts],
  );
  const unusedBytes = unused.reduce((sum, image) => sum + image.byteLength, 0);

  /*
   * Of those, the ones the SAVED text still shows. The server counts references
   * against the latest revision and the badge against the editor, and the two
   * disagree exactly when the author has removed a reference and not yet saved
   * - the case the server refuses without an acknowledgement, and therefore the
   * case the dialog has to warn about before it sends one.
   */
  const unusedButSaved = unused.filter((image) => (image.referenceCount ?? 0) > 0);

  /*
   * Thumbnails, for the rows that have been scrolled to.
   *
   * There is no thumbnail endpoint, so a thumbnail is the full image drawn
   * small - which is why only revealed rows are fetched. See useRevealedRows.
   */
  const revealed = useRevealedRows();
  const thumbnailable = useMemo(
    () => images.filter((image) => revealed.isRevealed(image.fileName)),
    [images, revealed],
  );
  const thumbnails = useImagePreviews(owner, thumbnailable, true);

  /*
   * Hovering a thumbnail shows the picture full size.
   *
   * The URL is looked up here rather than held in the hook, so the overlay can
   * only ever show bytes that are still loaded - see useHoverPreview.
   */
  const hover = useHoverPreview();
  const previewUrl = hover.activeKey === null ? undefined : thumbnails[hover.activeKey];

  /*
   * Asks the server what is attached now.
   *
   * Two things make this worth a button. An upload whose answer could not be read
   * leaves the browser genuinely unsure what was stored, and somebody else editing
   * the same article attaches images this page will never hear about - the article
   * is only re-read on a save.
   */
  const refresh = async (): Promise<void> => {
    const result = await action.run(() => owner.list());
    if (result) onImagesChanged(result.items);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;

    const result = await action.run(() =>
      owner.remove(pendingDelete.fileName, {
        // The server refuses to delete an image the article still uses unless the
        // warning has actually been shown. Sending the flag only when the count is
        // non-zero keeps it meaning what it says.
        acknowledgeReferences: pendingReferences > 0,
      }),
    );

    if (!result) return;

    onImagesChanged(images.filter((image) => image.fileName !== result.fileName));
    setDeletedNotice(words.deleted(result.fileName, result.danglingReferences));
    setPendingDelete(null);
  };

  /*
   * Every unused image in one request, all or nothing. The server refuses the
   * whole batch with a 409 if the saved text still shows any of them and the
   * warning was not on screen; here it is on screen exactly when unusedButSaved
   * is non-empty, so the flag means what it says, as it does for one image.
   *
   * The list afterwards is the server's answer, not the request: a name it
   * reports as already gone leaves the panel too, because it IS gone.
   */
  const confirmDeleteUnused = async (): Promise<void> => {
    if (unused.length === 0) return;

    const result = await action.run(() =>
      owner.removeMany(
        unused.map((image) => image.fileName),
        { acknowledgeReferences: unusedButSaved.length > 0 },
      ),
    );
    if (!result) return;

    const gone = new Set([...result.deleted, ...result.notAttached]);
    const deleted = new Set(result.deleted);
    const bytesFreed = images
      .filter((image) => deleted.has(image.fileName))
      .reduce((sum, image) => sum + image.byteLength, 0);

    onImagesChanged(images.filter((image) => !gone.has(image.fileName)));
    setDeletedNotice(describeBulkDelete(result, bytesFreed, t));
    setPendingBulk(false);
  };

  return (
    <div className="image-panel">
      <div className="card-header">
        <h3>{words.heading}</h3>
        <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
          {/* "n of 20": the ceiling is part of the count, so an author at 19 is not
              surprised by the refusal at 21 - the owner's own ceiling, which is
              ARTICLE_IMAGE_MAX_PER_ARTICLE or RELEASE_NOTE_IMAGE_MAX_PER_NOTE. */}
          <span className="faint" title={words.ceiling(noun, owner.maxImages)}>
            {images.length === 0
              ? words.noneAttached
              : words.count(images.length, owner.maxImages, t.article.byteSize(totalBytes))}
          </span>
          <button
            type="button"
            className="button button-small button-quiet"
            onClick={() => void refresh()}
            disabled={action.running}
            title={words.refreshHint}
          >
            {words.refresh}
          </button>
          {/*
            The bulk delete: the "not used" rows in one dialog rather than one
            each. Disabled rather than hidden when there is nothing to remove, so
            the header does not reflow as references are typed and deleted, and
            the title says why.
          */}
          <button
            type="button"
            className="button button-small button-quiet"
            onClick={() => setPendingBulk(true)}
            disabled={action.running || unused.length === 0}
            title={unused.length === 0 ? words.allUsed : words.deleteUnusedHint}
          >
            {words.deleteUnused(unused.length)}
          </button>
        </span>
      </div>

      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}

      {deletedNotice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{deletedNotice}</div>
          <div className="notice-actions">
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setDeletedNotice(null)}
            >
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {images.length === 0 ? (
        <p className="faint">{words.emptyHint(noun)}</p>
      ) : (
        <ul className="image-list">
          {images.map((image) => {
            const count = referenceCounts.get(image.fileName) ?? 0;
            return (
              <li
                key={image.fileName}
                className="image-list-row"
                ref={revealed.observe(image.fileName)}
              >
                {/*
                  A fixed-size cell, so the row does not jump sideways as
                  thumbnails arrive one by one. The image inside keeps its own
                  aspect ratio; the cell only bounds it.
                */}
                <span className="image-list-thumb" {...hover.handlers(image.fileName)}>
                  {thumbnails[image.fileName] ? (
                    <img
                      src={thumbnails[image.fileName]}
                      alt=""
                      /*
                        alt="" on purpose: the file name is right beside it in
                        the same row, so a screen reader announcing the picture
                        as well would only say it twice.
                      */
                    />
                  ) : null}
                </span>

                <div className="image-list-detail">
                  <code>{image.fileName}</code>
                  <span className="faint">
                    {t.article.byteSize(image.byteLength)} ·{' '}
                    {image.contentType.replace('image/', '').toUpperCase()} ·{' '}
                    <DateTime value={image.createdAtUtc} /> · {image.createdBy}
                  </span>
                </div>

                <div className="image-list-usage">
                  {count === 0 ? (
                    <span className="badge badge-unwritten" title={words.notUsedHint}>
                      {words.notUsed}
                    </span>
                  ) : (
                    <span className="faint">{words.used(count)}</span>
                  )}
                </div>

                <div className="button-row">
                  <button
                    type="button"
                    className="button button-small"
                    onClick={() => onInsert(image.fileName)}
                    title={words.insertHint}
                  >
                    {words.insert}
                  </button>
                  <button
                    type="button"
                    className="button button-small button-quiet"
                    onClick={() => setPendingDelete(image)}
                  >
                    {words.delete}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {previewUrl === undefined ? null : <ImageHoverPreview src={previewUrl} />}

      {pendingDelete && (
        <Modal
          open
          busy={action.running}
          onClose={() => setPendingDelete(null)}
          title={words.deleteTitle(pendingDelete.fileName)}
          footer={
            <>
              <span className="faint" style={{ marginRight: 'auto' }}>
                {words.nothingChangedYet}
              </span>
              <button
                type="button"
                className="button"
                onClick={() => setPendingDelete(null)}
                disabled={action.running}
              >
                {words.cancel}
              </button>
              <button
                type="button"
                className="button button-danger"
                onClick={() => void confirmDelete()}
                disabled={action.running}
              >
                {action.running ? words.deleting : words.deleteImage}
              </button>
            </>
          }
        >
          {pendingReferences > 0 && (
            <div className="notice notice-warning" role="alert">
              <div className="notice-body">
                <strong>{words.stillShown(pendingReferences)}</strong>
                <p style={{ marginBottom: 0 }}>{words.stillShownBody}</p>
              </div>
            </div>
          )}

          <p>
            {richText(words.details, {
              size: t.article.byteSize(pendingDelete.byteLength),
              type: pendingDelete.contentType.replace('image/', '').toUpperCase(),
              by: pendingDelete.createdBy,
              date: <DateTime value={pendingDelete.createdAtUtc} />,
            })}
          </p>

          <p>{words.deleteBody(noun)}</p>
        </Modal>
      )}

      {/*
        Listed by name with the size of each, because "delete 12 images" is not
        a decision anyone can take from the number alone - the author is looking
        for the one screenshot they meant to reference and forgot. Closes itself
        if a reference typed meanwhile empties the list.
      */}
      {pendingBulk && unused.length > 0 && (
        <Modal
          open
          busy={action.running}
          onClose={() => setPendingBulk(false)}
          title={words.bulkTitle(unused.length)}
          footer={
            <>
              <span className="faint" style={{ marginRight: 'auto' }}>
                {words.nothingChangedYet}
              </span>
              <button
                type="button"
                className="button"
                onClick={() => setPendingBulk(false)}
                disabled={action.running}
              >
                {words.cancel}
              </button>
              <button
                type="button"
                className="button button-danger"
                onClick={() => void confirmDeleteUnused()}
                disabled={action.running}
              >
                {action.running ? words.deleting : words.bulkConfirm(unused.length)}
              </button>
            </>
          }
        >
          {unusedButSaved.length > 0 && (
            <div className="notice notice-warning" role="alert">
              <div className="notice-body">
                <strong>{words.savedStillShows(unusedButSaved.length)}</strong>
                <p style={{ marginBottom: 0 }}>{words.savedStillShowsBody}</p>
              </div>
            </div>
          )}

          <p>{words.bulkIntro(unused.length, t.article.byteSize(unusedBytes))}</p>
          <ul style={{ margin: '0 0 1rem', paddingLeft: '1.25rem' }}>
            {unused.map((image) => (
              <li key={image.fileName}>
                <code>{image.fileName}</code>{' '}
                <span className="faint">{t.article.byteSize(image.byteLength)}</span>
              </li>
            ))}
          </ul>

          <p>{words.bulkBody(noun)}</p>
        </Modal>
      )}
    </div>
  );
}

/**
 * The sentence after a bulk delete. What went and what it weighed first; then
 * the two things the author may have to act on - names that were already gone
 * (somebody else's delete, or a Refresh that was due), and references the SAVED
 * text still carries, which are placeholders for readers until the text is
 * saved without them. The same wording the single delete uses for that.
 */
function describeBulkDelete(
  result: AdminArticleImageBulkDeleteResponse,
  bytesFreed: number,
  t: Messages,
): string {
  const words = t.admin.editor.images;
  const parts = [words.bulkDeleted(result.deleted.length, t.article.byteSize(bytesFreed))];

  const missing = result.notAttached.length;
  if (missing > 0) {
    parts.push(words.bulkAlreadyGone(missing));
  }

  if (result.danglingReferences > 0) {
    parts.push(words.bulkDangling(result.danglingReferences));
  }

  return parts.join(' ');
}
